/**
 * What recording a call needs from the browser (ADR 0067). Chrome shares the
 * system audio of the Mac only from version 141, on macOS 14.2 or later; the
 * macOS version is not readable from the browser.
 */
export type CallRecordingSupport =
  | { ok: true }
  | { ok: false; reason: "browser" | "chrome_version"; message: string };

const MAC_SYSTEM_AUDIO_CHROME = 141;

export function callRecordingSupport(input: {
  userAgent: string;
  hasDisplayMedia: boolean;
  hasMediaRecorder: boolean;
}): CallRecordingSupport {
  const chrome = /\bChrome\/(\d+)/.exec(input.userAgent);
  if (!chrome || !input.hasDisplayMedia || !input.hasMediaRecorder) {
    return {
      ok: false,
      reason: "browser",
      message: "Per registrare le chiamate apri Polaris in Google Chrome sul Mac.",
    };
  }
  if (/\bMacintosh\b/.test(input.userAgent) && Number(chrome[1]) < MAC_SYSTEM_AUDIO_CHROME) {
    return {
      ok: false,
      reason: "chrome_version",
      message: `Aggiorna Chrome: per sentire la voce del cliente serve la versione ${MAC_SYSTEM_AUDIO_CHROME} o successiva (Chrome → Informazioni su Google Chrome).`,
    };
  }
  return { ok: true };
}

/** The first audio format the browser records among those Polaris takes. */
export function recorderMimeType(isTypeSupported: (type: string) => boolean): string | null {
  return (
    ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4"].find((type) =>
      isTypeSupported(type),
    ) ?? null
  );
}

/** One byte per sample, as base64: what the server reads in `activity.levels`. */
export function levelsToBase64(levels: readonly number[]): string {
  let binary = "";
  for (let start = 0; start < levels.length; start += 4_096) {
    binary += String.fromCharCode(...levels.slice(start, start + 4_096));
  }
  return btoa(binary);
}

/** "4:05", "1:02:03". */
export function formatCallDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1_000));
  const hours = Math.floor(total / 3_600);
  const minutes = Math.floor((total % 3_600) / 60);
  const seconds = String(total % 60).padStart(2, "0");
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${seconds}`
    : `${minutes}:${seconds}`;
}

/** "12 min", "meno di un minuto". */
export function callMinutesLabel(ms: number | null): string {
  if (ms === null) return "";
  const minutes = Math.round(ms / 60_000);
  return minutes < 1 ? "meno di un minuto" : `${minutes} min`;
}

const LEVELS_PER_DB = 255 / 70;

/** RMS of samples in -1…1 as the byte of the volume tracks: 0 is -70 dBFS or less, 255 is 0 dBFS. */
export function levelFromRms(rms: number): number {
  const db = rms > 0 ? 20 * Math.log10(rms) : -100;
  return Math.max(0, Math.min(255, Math.round((db + 70) * LEVELS_PER_DB)));
}

/** How many of `segments` lights of a meter are on, from -60 to -10 dBFS. */
export function meterSegments(level: number, segments = 8): number {
  const db = level / LEVELS_PER_DB - 70;
  return Math.max(0, Math.min(segments, Math.ceil(((db + 60) / 50) * segments)));
}

/** Speech of one source: above its own background, which it learns from the last 30 s. */
class SpeechGate {
  private readonly history: number[] = [];

  constructor(private readonly window: number) {}

  push(level: number): boolean {
    this.history.push(level);
    if (this.history.length > this.window) this.history.shift();
    const sorted = [...this.history].sort((left, right) => left - right);
    const floor = sorted[Math.floor(sorted.length * 0.1)] ?? 0;
    // 10 dB above the background, and never under -50 dBFS.
    return level > Math.max(floor + 10 * LEVELS_PER_DB, 20 * LEVELS_PER_DB);
  }
}

/**
 * Where to end a part of the recording (ADR 0067): only while both voices are
 * silent, so no word is cut. From `minMs` on it waits for `silenceMs` of silence,
 * from `relaxedMs` on for `relaxedSilenceMs`; at `maxMs` it cuts anyway.
 */
export class PartCutter {
  private readonly member: SpeechGate;
  private readonly contact: SpeechGate;
  private samples = 0;
  private silent = 0;

  constructor(
    private readonly options: {
      stepMs: number;
      minMs: number;
      relaxedMs: number;
      maxMs: number;
      silenceMs: number;
      relaxedSilenceMs: number;
    },
  ) {
    const window = Math.round(30_000 / options.stepMs);
    this.member = new SpeechGate(window);
    this.contact = new SpeechGate(window);
  }

  /** One volume sample of each source; true when the part ends here. */
  push(member: number, contact: number): boolean {
    const memberSpeaks = this.member.push(member);
    const contactSpeaks = this.contact.push(contact);
    this.samples += 1;
    this.silent = memberSpeaks || contactSpeaks ? 0 : this.silent + 1;
    const elapsed = this.samples * this.options.stepMs;
    if (elapsed >= this.options.maxMs) return true;
    if (elapsed < this.options.minMs) return false;
    const needed =
      elapsed >= this.options.relaxedMs ? this.options.relaxedSilenceMs : this.options.silenceMs;
    return this.silent * this.options.stepMs >= needed;
  }

  /** A new part starts: the backgrounds learnt so far stay. */
  restart(): void {
    this.samples = 0;
    this.silent = 0;
  }
}
